import React, { useState, useRef } from 'react';
import {
  Box, Button, Typography, Alert, LinearProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from '@mui/material';
import { CloudUpload, Download, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import DrawerShell from './_drawer/DrawerShell';
import DrawerHeader from './_drawer/DrawerHeader';
import useResponsiveAnchor from './_drawer/useResponsiveAnchor';
import api from '../../utils/api';
import { RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

function BulkImportDrawer({ open, onClose, onSuccess, showSnackbar }) {
  const anchor = useResponsiveAnchor();
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    setSelectedFile(null);
    setResult(null);
    onClose();
  };

  const handleDownloadSample = () => {
    const csv = 'name,phone,email\nJohn Doe,9876543210,john@example.com\nJane Smith,9123456789,jane@example.com\nRahul Kumar,8765432190,';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      showSnackbar('Please select a CSV file', 'error');
      return;
    }
    setSelectedFile(file);
    setResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await api.post('/librarian/students/bulk-import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data.data);
      if (res.data.data.created > 0) {
        showSnackbar(`${res.data.data.created} students imported successfully`, 'success');
        onSuccess();
      }
    } catch (err) {
      showSnackbar(err.response?.data?.message || 'Failed to import students', 'error');
    } finally {
      setUploading(false);
    }
  };

  const footer = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button onClick={handleClose} sx={{ borderRadius: RADIUS.md }}>
        {result ? 'Done' : 'Cancel'}
      </Button>
      {!result && (
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          startIcon={<CloudUpload />}
          fullWidth
          sx={{ borderRadius: RADIUS.md, background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)' }}
        >
          {uploading ? 'Importing...' : 'Import Students'}
        </Button>
      )}
    </Box>
  );

  return (
    <DrawerShell open={open} onClose={handleClose} anchor={anchor} ariaLabel="Bulk import students" footer={footer}>
      <DrawerHeader title="Bulk Import Students" onClose={handleClose} />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Upload a CSV with columns: <strong>name</strong>, <strong>phone</strong>, <strong>email</strong>.
          Default password for all imported students: <strong>Welcome@123</strong>.
        </Alert>

        <Button
          variant="outlined"
          startIcon={<Download />}
          onClick={handleDownloadSample}
          fullWidth
          sx={{ mb: 2, borderRadius: RADIUS.md }}
        >
          Download Sample CSV
        </Button>

        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: selectedFile ? 'success.main' : 'divider',
            borderRadius: RADIUS.lg,
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: selectedFile ? 'success.50' : 'action.hover',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.selected' },
            mb: 2,
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <CloudUpload sx={{ fontSize: 40, color: selectedFile ? 'success.main' : 'text.secondary', mb: 1 }} />
          {selectedFile ? (
            <Box>
              <Typography variant="body1" fontWeight="600" color="success.main">
                {selectedFile.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(selectedFile.size / 1024).toFixed(1)} KB — Click to change
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body1" fontWeight="600">Click to select CSV file</Typography>
              <Typography variant="caption" color="text.secondary">Maximum file size: 2MB</Typography>
            </Box>
          )}
        </Box>

        {uploading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

        {result && (
          <Box>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Chip icon={<CheckCircle />} label={`${result.created} Created`} color="success" />
              {result.skipped > 0 && (
                <Chip icon={<ErrorIcon />} label={`${result.skipped} Skipped`} color="warning" />
              )}
            </Box>

            {result.errors?.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Row</strong></TableCell>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((err, i) => (
                      <TableRow key={i}>
                        <TableCell>{err.row}</TableCell>
                        <TableCell>{err.name || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color="error">{err.reason}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Box>
    </DrawerShell>
  );
}

export default BulkImportDrawer;
